import { useQueryClient } from '@tanstack/react-query';
import { getQueryKey } from '@trpc/react-query';

import { api } from '@/utils/api';

export const useCadenceDeleteAllMutation = () => {
  const utils = api.useUtils();
  return api.cadenceBulk.bulkDelete.useMutation({
    onSuccess: async () => {

      await Promise.all([
        utils.cadence.cadences2.invalidate(),
        utils.cadence.cadencesForCards.invalidate(),
        utils.cadence.fetchPaginatedExecutables.invalidate(),
        utils.cadence.nextExecutable.invalidate(),
        utils.globalReminder.paginated.invalidate(),
        utils.contact.contentViewed.invalidate(),
        utils.contact.responded.invalidate(),
        utils.contact.byId.invalidate(),
        utils.contact.search.invalidate(),
        utils.contact.interrupted.invalidate(),
        utils.history.contact.invalidate(),
      ]);
      void utils.cadenceTemplate.idV2.invalidate();
      void utils.cadenceTemplate.analytics.invalidate();
      void utils.steps.universalNextCadenceOne.invalidate();
      void utils.steps.universalNextOne.invalidate();
    },
  });
};

export const useCadenceDeleteMutation = () => {
  const utils = api.useUtils();
  return api.cadenceBulk.delete.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.cadence.cadences2.invalidate(),
        utils.cadence.cadencesForCards.invalidate(),
        utils.cadence.fetchPaginatedExecutables.invalidate(),
        utils.cadence.nextExecutable.invalidate(),
        utils.globalReminder.paginated.invalidate(),
        utils.contact.contentViewed.invalidate(),
        utils.contact.responded.invalidate(),
        utils.contact.byId.invalidate(),
        utils.contact.interrupted.invalidate(),
        utils.history.contact.invalidate(),
        utils.cadenceTemplate.candidateListForContact.invalidate(),
        utils.cadenceTemplate.candidateCounts.invalidate(),
      ]);
      void utils.cadenceTemplate.idV2.invalidate();

      void utils.steps.universalNextCadenceOne.invalidate();
      void utils.steps.universalNextOne.invalidate();
    },
  });
};

export const useCadenceDeleteFutureStepsMutation = () => {
  const utils = api.useUtils();
  return api.cadence.deleteFutureSteps.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.cadence.cadences2.invalidate(),
        utils.cadence.cadencesForCards.invalidate(),
        utils.cadence.fetchPaginatedExecutables.invalidate(),
        utils.cadence.nextExecutable.invalidate(),
        utils.globalReminder.paginated.invalidate(),
        utils.contact.contentViewed.invalidate(),
        utils.contact.responded.invalidate(),
        utils.contact.byId.invalidate(),
        utils.contact.interrupted.invalidate(),
      ]);
      void utils.cadenceTemplate.idV2.invalidate();

      void utils.steps.universalNextCadenceOne.invalidate();
      void utils.steps.universalNextOne.invalidate();
    },
  });
};

export const useCadenceChangeUserBulkMutation = () => {
  const utils = api.useUtils();
  return api.cadenceBulk.changeUserBulk.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.cadence.cadences2.invalidate(),
        utils.cadence.cadencesForCards.invalidate(),
        utils.cadence.fetchPaginatedExecutables.invalidate(),
        utils.contact.byId.invalidate(),
        utils.contact.interrupted.invalidate(),
        utils.contact.getConnected.invalidate(),
        utils.contact.getConnectedGroupedByOrg.invalidate(),
      ]);

      void utils.steps.universalNextCadenceOne.invalidate();
      void utils.steps.universalNextOne.invalidate();
    },
  });
};

export const useCadencePauseAllMutation = () => {
  const utils = api.useUtils();
  return api.cadenceBulk.bulkPause.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.cadence.cadences2.invalidate(),
        utils.cadence.cadencesForCards.invalidate(),
        utils.contact.byId.invalidate(),
        utils.history.contact.invalidate(),
      ]);

      void utils.cadenceTemplate.analytics.invalidate();

      void utils.cadence.fetchPaginatedExecutables.invalidate();
      void utils.cadence.nextExecutable.invalidate();
      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
      void utils.globalReminder.paginated.invalidate();

      void utils.steps.universalNextCadenceOne.invalidate();
      void utils.steps.universalNextOne.invalidate();
      void utils.contact.interrupted.invalidate();
    },
  });
};

export const useCadenceReattachMutation = () => {
  const utils = api.useUtils();
  return api.cadence.updateState.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.cadence.cadences2.invalidate(),
        utils.cadence.cadencesForCards.invalidate(),
        utils.contact.byId.invalidate(),
        utils.history.contact.invalidate(),
      ]);

      void utils.cadence.fetchPaginatedExecutables.invalidate();
      void utils.cadence.nextExecutable.invalidate();
      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
      void utils.globalReminder.paginated.invalidate();

      // Reattaching changes execution state — mirror the pause/resume siblings so the
      // interrupted list and next-executable queries refresh too.
      void utils.contact.interrupted.invalidate();
      void utils.steps.universalNextCadenceOne.invalidate();
      void utils.steps.universalNextOne.invalidate();
    },
  });
};

export const useCadenceResumeAllMutation = () => {
  const utils = api.useUtils();
  return api.cadenceBulk.bulkResume.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.contact.byId.invalidate(),
        utils.cadence.cadences2.invalidate(),
        utils.cadence.cadencesForCards.invalidate(),
        utils.history.contact.invalidate(),
      ]);

      void utils.cadenceTemplate.analytics.invalidate();

      void utils.cadence.fetchPaginatedExecutables.invalidate();
      void utils.cadence.nextExecutable.invalidate();
      void utils.contact.contentViewed.invalidate();
      void utils.contact.responded.invalidate();
      void utils.globalReminder.paginated.invalidate();

      void utils.steps.universalNextCadenceOne.invalidate();
      void utils.steps.universalNextOne.invalidate();
      void utils.contact.interrupted.invalidate();
    },
  });
};

export const useBulkChangeCadenceMutation = () => {
  const utils = api.useUtils();
  return api.cadenceBulk.bulkChangeCadence.useMutation({
    onSuccess: async () => {
      await Promise.all([
        utils.cadence.cadences2.invalidate(),
        utils.cadence.cadencesForCards.invalidate(),
        utils.cadence.fetchPaginatedExecutables.invalidate(),
        utils.cadence.nextExecutable.invalidate(),
        utils.cadenceTemplate.idV2.invalidate(),
        utils.cadenceTemplate.candidateCounts.invalidate(),
        utils.cadenceTemplate.candidateList.invalidate(),
        utils.globalReminder.paginated.invalidate(),
        utils.contact.contentViewed.invalidate(),
        utils.contact.byId.invalidate(),

        utils.contact.search.invalidate(),
        utils.steps.universalNextCadenceOne.invalidate(),
        utils.steps.universalNextOne.invalidate(),
        utils.contact.interrupted.invalidate(),
      ]);
    },
  });
};

export const useCadenceDeleteStepMutation = () => {
  const utils = api.useUtils();
  return api.cadence.deleteStep.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });

      void utils.cadence.cadences2.invalidate();
      void utils.cadence.fetchPaginatedExecutables.invalidate();
      void utils.cadence.nextExecutable.invalidate();

      void utils.steps.universalNextCadenceOne.invalidate({ cadenceId: data.cadenceId });
      void utils.steps.universalNextCadenceOne.invalidate({ contactId: data.contactId });
      void utils.steps.universalNextOne.invalidate({ teamId: data.teamId });
    },
  });
};

export const useCadenceCreateStepMutation = () => {
  const utils = api.useUtils();
  return api.cadence.createStep.useMutation({
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });

      void utils.cadence.cadences2.invalidate();
      void utils.cadence.fetchPaginatedExecutables.invalidate();
      void utils.cadence.nextExecutable.invalidate();

      void utils.steps.universalNextCadenceOne.invalidate({ cadenceId: data.cadenceId });
      void utils.steps.universalNextCadenceOne.invalidate({ contactId: data.contactId });
      void utils.steps.universalNextOne.invalidate({ teamId: data.teamId });
    },
  });
};
export const useCadenceChangeStepStateMutation = () => {
  const utils = api.useUtils();
  return api.cadenceBulk.bulkStepChangeState.useMutation({
    onSuccess: () => {
      // utils.contact.byId.setData({ contactId: data.contact.id }, () => data);
      void utils.contact.byId.invalidate();
      void utils.cadence.cadences2.invalidate();
      void utils.cadence.fetchPaginatedExecutables.invalidate();
      void utils.cadence.nextExecutable.invalidate();

      void utils.steps.universalNextCadenceOne.invalidate();
      void utils.steps.universalNextOne.invalidate();
    },
  });
};
export const useCadenceUpdateStepMutation = () => {
  const utils = api.useUtils();
  const queryClient = useQueryClient();
  // Resolved once at hook init — `getQueryKey(api.contact.byId)` returns
  // the operation prefix (no input filtering), so `setQueriesData`
  // updates EVERY cached `contact.byId` entry that contains the step.
  // The contact-detail page calls byId with a 4-field input
  // (contactId / cadenceId / userId / teamId); a partial-input setData
  // wouldn't match it, so we go through the underlying queryClient.
  const byIdQueryKey = getQueryKey(api.contact.byId);

  return api.cadence.updateStep.useMutation({
    // OPTIMISTIC UPDATE
    // Patch any cached contact.byId entry that contains this step so
    // the editor closes and the new content shows up instantly,
    // without waiting for the network round-trip + refetch.
    onMutate: async ({ stepId, data }) => {
      await queryClient.cancelQueries({ queryKey: byIdQueryKey });
      const snapshots = queryClient.getQueriesData({ queryKey: byIdQueryKey });

      queryClient.setQueriesData(
        { queryKey: byIdQueryKey },
        (old: unknown) => {
          if (old == null || typeof old !== 'object') return old;
          const oldRec = old as { activeCadence?: { cadenceSteps?: unknown[] | null } | null };
          const steps = oldRec.activeCadence?.cadenceSteps;
          if (!Array.isArray(steps)) return old;
          let touched = false;
          const updatedSteps = steps.map((s) => {
            const step = s as Record<string, unknown> & { id: string };
            if (step.id !== stepId) return s;
            touched = true;
            // Patch every server-accepted field that's in the payload.
            // body2 / subject / cc / bcc / notes can be empty strings,
            // and that's the user's intent — preserve as-is. Only skip
            // when the field is genuinely undefined.
            const patch: Record<string, unknown> = {};
            if (data.body2 !== undefined) patch.body2 = data.body2;
            if (data.subject !== undefined) patch.subject = data.subject;
            if (data.cc !== undefined) patch.cc = data.cc;
            if (data.bcc !== undefined) patch.bcc = data.bcc;
            if (data.notes !== undefined) patch.notes = data.notes;
            if (data.callStatus !== undefined) patch.callStatus = data.callStatus;
            if (data.postsToLike !== undefined) patch.postsToLike = data.postsToLike;
            if (data.onlyOpenLink !== undefined) patch.onlyOpenLink = data.onlyOpenLink;
            if (data.useEmailSignature !== undefined)
              patch.useEmailSignature = data.useEmailSignature;
            if (data.type !== undefined) patch.type = data.type;
            if (data.delay !== undefined) patch.delay = data.delay;
            if (data.siftConditions !== undefined) patch.siftConditions = data.siftConditions;
            return { ...step, ...patch };
          });
          if (!touched) return old;
          return {
            ...oldRec,
            activeCadence: { ...oldRec.activeCadence, cadenceSteps: updatedSteps },
          };
        },
      );

      return { snapshots };
    },
    // ROLLBACK on failure: restore every snapshot we took. The server
    // throws on validation failures (empty body for message, etc.) so
    // the rollback puts the old content back instantly.
    onError: (_err, _vars, ctx) => {
      const snapshots = (ctx as { snapshots?: ReturnType<typeof queryClient.getQueriesData> })
        ?.snapshots;
      if (!snapshots) return;
      for (const [key, data] of snapshots) {
        queryClient.setQueryData(key, data);
      }
    },
    onSuccess: (data) => {
      void utils.contact.byId.invalidate({ contactId: data.contactId });

      void utils.cadence.cadences2.invalidate();
      void utils.cadence.fetchPaginatedExecutables.invalidate();
      void utils.cadence.nextExecutable.invalidate();

      // void utils.contact.algoliaSearch.invalidate();
      void utils.globalReminder.paginated.invalidate();
      void utils.contact.contentViewed.invalidate();

      void utils.steps.universalNextCadenceOne.invalidate({ cadenceId: data.cadenceId });
      void utils.steps.universalNextCadenceOne.invalidate({ contactId: data.contactId });
      void utils.steps.universalNextOne.invalidate({ teamId: data.teamId });
    },
  });
};

export const useChangeCadenceOwnerMutation = () => {
  const utils = api.useUtils();
  return api.cadence.changeUser.useMutation({
    onSuccess: () => {
      void utils.contact.byId.invalidate();
      void utils.cadence.cadences2.invalidate();
      void utils.cadence.fetchPaginatedExecutables.invalidate();
      void utils.cadence.nextExecutable.invalidate();
    },
  });
};